<template>
  <ExpensesPageShell
    :title="pageTitle"
    :subtitle="pageSubtitle"
    :actions="headerActions"
    @action="onHeaderAction"
  >
    <template #metrics>
      <section class="metrics-grid">
        <SalesKpiCard
          v-for="metric in kpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-route="metric.valueRoute"
          :value-link-class="metric.valueLinkClass"
          :value-scale="metric.valueScale"
          :truncate-value="metric.truncateValue"
          :value-meta="metric.valueMeta"
          :value-meta-lines="metric.valueMetaLines"
          :extra-values="metric.extraValues"
          :value-segments="metric.valueSegments"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <FeesToolbar
        :filter-options="filterOptions"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />

      <StatusTabs
        :tabs="feeStatusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <FeesTable
        :rows="paginatedRows"
        :loading="tableLoading"
        :pagination="feesPagination"
        :use-external-sort="true"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        :empty-text="text.table.empty"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
        @pay-expense="handlePayFee"
        @edit-expense="handleEditFee"
        @delete-expense="handleDeleteFee"
        @approve-expense="handleApproveFee"
        @download-xml="handleDownloadXml"
        @download-pdf="handleDownloadPdf"
        @download-receipt="handleDownloadReceipt"
      />
    </template>

    <template #modal>
      <FeeModal
        :show="isFeeModalOpen"
        :mode="feeModalMode"
        :initial-data="feeModalInitialData"
        :submitting="isFeeSubmitting"
        @close="closeFeeModal"
        @submit="submitFee"
      />

      <ConfirmActionModal
        :show="isDeleteFeeModalOpen"
        :title="pageConfirmDeleteTitle"
        :subtitle="pageConfirmDeleteSubtitle"
        :message="deleteFeeModalMessage"
        :confirm-text="deleteFeeConfirmText"
        :cancel-text="pageConfirmCancelText"
        @cancel="closeDeleteFeeModal"
        @confirm="confirmDeleteFee"
      />

      <ConfirmActionModal
        :show="isApproveFeeModalOpen"
        :title="approveFeeModalTitle"
        :subtitle="approveFeeModalSubtitle"
        :message="approveFeeModalMessage"
        :confirm-text="approveFeeConfirmText"
        :cancel-text="pageConfirmCancelText"
        @cancel="closeApproveFeeModal"
        @confirm="confirmApproveFee"
      />

      <PayModal
        :show="isPayFeeModalOpen"
        :row="payFeeTarget"
        context="expense-fee"
        entity-type="fee"
        :submitting="isPayFeeSubmitting"
        @close="closePayFeeModal"
        @submit="submitPayFee"
      />
    </template>
  </ExpensesPageShell>
</template>

<script>
import ExpensesPageShell from '@/components/expenses/ExpensesPageShell.vue';
import FeesToolbar from '@/components/fees/FeesToolbar.vue';
import FeesTable from '@/components/fees/FeesTable.vue';
import FeeModal from '@/components/fees/FeeModal.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import { formatCurrency } from '@/utils/numberFormat';
import SalesKpiCard from '@/components/sales/SalesKpiCard.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import {
  buildExpenseFeeListParams,
  createDefaultExpenseFeeToolbarFilters,
  normalizeExpenseFeeToolbarFilters,
} from '@/composables/expensesFees/shared/useExpenseFeeFilters';
import {
  fetchFeesSnapshot,
  getFeesFilterOptions,
  fetchFeesFilterOptions,
} from '@/composables/fees/useFeesList';
import {
  buildExpenseFeeStatusTabs as buildFeeStatusTabs,
  filterExpenseFeeByStatus as filterFeesByStatus,
} from '@/composables/expensesFees/shared/useExpenseFeeStatusTabs';
import {
  createInitialFeesMetricsValues,
  mapFeesMetricsPayload,
  buildFeesKpiCards,
} from '@/composables/fees/useFeesMetrics';
import {
  confirmFeeApproveFlow,
  confirmFeeDeleteFlow,
  closeFeeModalState,
  notifyFeeActionPending,
  openFeeEditModalFromRow,
  openFeeModalState,
  resolveFeeApprovalTargetState,
  submitFeeFlow,
  submitFeePayFlow,
} from '@/composables/fees/useFeeFlow';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import {
  resolveSortChange,
  sortRows,
} from '@/composables/shared/useSorting';
import { FEES_TEXT } from '@/texts/catalog/fees';

const FEES_FLOW_TEXT = FEES_TEXT.flow;

const FEE_FILTERS_OPTIONS = Object.freeze({ includeCollaborator: true });

export default {
  name: 'FeesPage',
  components: {
    ExpensesPageShell,
    FeesToolbar,
    FeesTable,
    FeeModal,
    PayModal,
    ConfirmActionModal,
    SalesKpiCard,
    StatusTabs,
  },
  data() {
    return {
      headerActions: [
        {
          key: 'new-fee',
          label: String(FEES_TEXT?.page?.actions?.newFee || '').trim(),
          icon: 'fa-plus',
          variant: 'primary',
        },
      ],
      tableLoading: false,
      feeRows: [],
      toolbarSearch: '',
      toolbarFilters: createDefaultExpenseFeeToolbarFilters(FEE_FILTERS_OPTIONS),
      appliedToolbarSearch: '',
      appliedToolbarFilters: createDefaultExpenseFeeToolbarFilters(FEE_FILTERS_OPTIONS),
      activeStatusTab: 'all',
      sortField: 'date',
      sortDirection: 'asc',
      listRequestToken: 0,
      currentPage: 1,
      pageSize: 20,
      metricsValues: createInitialFeesMetricsValues(),
      filterOptions: getFeesFilterOptions(),
      isFeeModalOpen: false,
      isFeeSubmitting: false,
      feeModalMode: 'create',
      feeModalInitialData: null,
      isDeleteFeeModalOpen: false,
      isDeletingFee: false,
      deleteFeeTarget: null,
      isApproveFeeModalOpen: false,
      isApprovingFee: false,
      approveFeeTarget: null,
      approveFeeTargetState: true,
      isPayFeeModalOpen: false,
      isPayFeeSubmitting: false,
      payFeeTarget: null,
      text: FEES_TEXT,
    };
  },
  computed: {
    pageText() {
      return this.text?.page && typeof this.text.page === 'object'
        ? this.text.page
        : {};
    },
    pageConfirm() {
      return this.pageText?.confirm && typeof this.pageText.confirm === 'object'
        ? this.pageText.confirm
        : {};
    },
    pageConfirmDelete() {
      return this.pageConfirm?.delete && typeof this.pageConfirm.delete === 'object'
        ? this.pageConfirm.delete
        : {};
    },
    pageConfirmApprove() {
      return this.pageConfirm?.approve && typeof this.pageConfirm.approve === 'object'
        ? this.pageConfirm.approve
        : {};
    },
    pageTitle() {
      return String(this.pageText?.title || '').trim();
    },
    pageSubtitle() {
      return String(this.pageText?.subtitle || '').trim();
    },
    pageConfirmDeleteTitle() {
      return String(this.pageConfirmDelete?.title || '').trim();
    },
    pageConfirmDeleteSubtitle() {
      return String(this.pageConfirmDelete?.subtitle || '').trim();
    },
    pageConfirmCancelText() {
      return String(this.pageConfirm?.cancel || '').trim();
    },
    normalizedRows() {
      return Array.isArray(this.feeRows) ? this.feeRows : [];
    },
    feeStatusTabs() {
      return buildFeeStatusTabs(this.searchedRows);
    },
    searchedRows() {
      const search = String(this.toolbarSearch || '').trim().toLowerCase();
      if (search === '') {
        return this.normalizedRows;
      }

      return this.normalizedRows.filter((row) => {
        const haystack = [
          row?.provider,
          row?.collaborator,
          row?.commission_agent_name,
          row?.concept,
          row?.folio,
          row?.reference,
          row?.customer,
          row?.sale_name,
          row?.sale_code,
        ]
          .map((value) => String(value || '').trim().toLowerCase())
          .join(' ');

        return haystack.includes(search);
      });
    },
    filteredRows() {
      return filterFeesByStatus(this.searchedRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.filteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        date: (row) => new Date(String(row?.date || '')).getTime() || 0,
        provider: (row) => String(row?.provider || '').trim(),
        subtotal: (row) => Number(row?.subtotal || 0),
        total: (row) => Number(row?.total || 0),
        payment_date: (row) => new Date(String(row?.payment_date || '')).getTime() || 0,
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    feesPagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
    kpiCards() {
      const cards = buildFeesKpiCards(this.metricsValues);
      return Array.isArray(cards)
        ? cards.filter((card) => card && typeof card === 'object')
        : [];
    },
    deleteFeeModalMessage() {
      const row = this.deleteFeeTarget;
      if (!row || typeof row !== 'object') {
        return String(this.pageConfirmDelete?.messageDefault || '').trim();
      }

      const provider = String(row?.provider || row?.collaborator || '').trim() || String(this.pageConfirm?.noProvider || '').trim();
      const concept = String(row?.concept || '').trim() || String(this.pageConfirm?.noConcept || '').trim();
      const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
        currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
      });

      const summaryResolver = this.pageConfirm?.summary;
      return typeof summaryResolver === 'function'
        ? String(summaryResolver({ provider, concept, amount }) || '').trim()
        : '';
    },
    deleteFeeConfirmText() {
      return this.isDeletingFee
        ? String(this.pageConfirmDelete?.confirming || '').trim()
        : String(this.pageConfirmDelete?.confirm || '').trim();
    },
    approveFeeModalTitle() {
      return this.approveFeeTargetState
        ? String(this.pageConfirmApprove?.titleApprove || '').trim()
        : String(this.pageConfirmApprove?.titleUnapprove || '').trim();
    },
    approveFeeModalSubtitle() {
      return this.approveFeeTargetState
        ? String(this.pageConfirmApprove?.subtitleApprove || '').trim()
        : String(this.pageConfirmApprove?.subtitleUnapprove || '').trim();
    },
    approveFeeModalMessage() {
      const row = this.approveFeeTarget;
      if (!row || typeof row !== 'object') {
        return this.approveFeeTargetState
          ? String(this.pageConfirmApprove?.messageApproveDefault || '').trim()
          : String(this.pageConfirmApprove?.messageUnapproveDefault || '').trim();
      }

      const provider = String(row?.provider || row?.collaborator || '').trim() || String(this.pageConfirm?.noProvider || '').trim();
      const concept = String(row?.concept || '').trim() || String(this.pageConfirm?.noConcept || '').trim();
      const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
        currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
      });

      const summaryResolver = this.pageConfirm?.summary;
      return typeof summaryResolver === 'function'
        ? String(summaryResolver({ provider, concept, amount }) || '').trim()
        : '';
    },
    approveFeeConfirmText() {
      if (this.isApprovingFee) {
        return this.approveFeeTargetState
          ? String(this.pageConfirmApprove?.confirmingApprove || '').trim()
          : String(this.pageConfirmApprove?.confirmingUpdate || '').trim();
      }

      return this.approveFeeTargetState
        ? String(this.pageConfirmApprove?.confirmApprove || '').trim()
        : String(this.pageConfirmApprove?.confirmUnapprove || '').trim();
    },
  },
  mounted() {
    this.loadFilterOptions();
    if (this.applySupplierFilterFromRoute()) {
      this.fetchFees();
    }
  },
  beforeUnmount() {
    // no-op
  },
  methods: {
    onHeaderAction(action) {
      const key = String(action?.key || '').trim();

      if (key === 'new-fee') {
        this.openFeeModal();
      }
    },
    openFeeModal(mode = 'create', initialData = null) {
      openFeeModalState(this, mode, initialData);
    },
    closeFeeModal() {
      closeFeeModalState(this);
    },
    closeDeleteFeeModal() {
      if (this.isDeletingFee) {
        return;
      }

      this.isDeleteFeeModalOpen = false;
      this.deleteFeeTarget = null;
    },
    closeApproveFeeModal() {
      if (this.isApprovingFee) {
        return;
      }

      this.isApproveFeeModalOpen = false;
      this.approveFeeTarget = null;
      this.approveFeeTargetState = true;
    },
    closePayFeeModal() {
      if (this.isPayFeeSubmitting) {
        return;
      }

      this.isPayFeeModalOpen = false;
      this.isPayFeeSubmitting = false;
      this.payFeeTarget = null;
    },
    async submitPayFee(payload = {}) {
      await submitFeePayFlow(this, payload, {
        onSuccess: async () => {
          await this.fetchFees();
        },
      });
    },
    resolveApprovalTargetState(row, explicitState = null) {
      return resolveFeeApprovalTargetState(row, explicitState);
    },
    async submitFee(payload) {
      await submitFeeFlow(this, payload, {
        onSuccess: async () => {
          await this.fetchFees();
        },
      });
    },
    ensureValidPage() {
      this.currentPage = ensureValidPage(this.currentPage, this.totalPages);
    },
    buildListParams() {
      return buildExpenseFeeListParams({
        toolbarFilters: this.appliedToolbarFilters,
        toolbarSearch: this.appliedToolbarSearch,
        statusTab: this.activeStatusTab,
        includeCollaborator: true,
      });
    },
    selectStatusTab(tabKey) {
      const normalized = String(tabKey || '').trim();
      if (normalized === '' || normalized === this.activeStatusTab) {
        return;
      }

      this.activeStatusTab = normalized;
      this.currentPage = 1;
    },
    async loadFilterOptions() {
      try {
        this.filterOptions = await fetchFeesFilterOptions();
      } catch (error) {
        this.filterOptions = getFeesFilterOptions();
      }
    },
    applySupplierFilterFromRoute() {
      const supplierId = Number(this.$route?.query?.supplier_id || 0);
      if (!Number.isFinite(supplierId) || supplierId <= 0) {
        return false;
      }

      const nextFilters = normalizeExpenseFeeToolbarFilters({
        ...this.toolbarFilters,
        provider: [supplierId],
      }, FEE_FILTERS_OPTIONS);

      this.toolbarFilters = nextFilters;
      this.appliedToolbarFilters = nextFilters;
      this.currentPage = 1;

      return true;
    },
    async fetchFees() {
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const snapshot = await fetchFeesSnapshot(this.buildListParams());

        if (requestToken === this.listRequestToken) {
          const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
          this.feeRows = rows;
          this.metricsValues = mapFeesMetricsPayload(snapshot?.metrics || {});
          this.ensureValidPage();
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.feeRows = [];
          this.metricsValues = createInitialFeesMetricsValues();
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
        }
      }
    },
    handleToolbarSearch(value) {
      const nextSearch = String(value || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
    },
    handleToolbarFilters(filters, meta = {}) {
      const previousDateStart = String(this.toolbarFilters?.date_start || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_end || '').trim();
      const normalized = normalizeExpenseFeeToolbarFilters(filters, FEE_FILTERS_OPTIONS);
      this.toolbarFilters = normalized;

      if (Object.prototype.hasOwnProperty.call(normalized, 'search')) {
        this.toolbarSearch = String(normalized.search || '');
      }

      const nextDateStart = String(normalized.date_start || '').trim();
      const nextDateEnd = String(normalized.date_end || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;
      const trigger = String(meta?.trigger || '').trim();

      if (dateChanged && trigger === 'date') {
        this.appliedToolbarFilters = normalized;
        this.appliedToolbarSearch = String(this.toolbarSearch || '');
        this.currentPage = 1;
        this.fetchFees();
      }
    },
    applyToolbarFilters(payload = {}) {
      const search = Object.prototype.hasOwnProperty.call(payload, 'search')
        ? String(payload.search || '')
        : String(this.toolbarSearch || '');

      const filters = Object.prototype.hasOwnProperty.call(payload, 'filters')
        ? normalizeExpenseFeeToolbarFilters(payload.filters, FEE_FILTERS_OPTIONS)
        : normalizeExpenseFeeToolbarFilters(this.toolbarFilters, FEE_FILTERS_OPTIONS);

      this.toolbarSearch = search;
      this.toolbarFilters = filters;
      this.appliedToolbarSearch = search;
      this.appliedToolbarFilters = filters;
      this.currentPage = 1;
      this.fetchFees();
    },
    handlePageChange(nextPage) {
      this.currentPage = Number(nextPage || 1);
      this.ensureValidPage();
    },
    handleSortChange(payload) {
      const nextSort = resolveSortChange(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        payload,
        ['date', 'provider', 'subtotal', 'total', 'payment_date'],
      );

      if (nextSort.field === this.sortField && nextSort.direction === this.sortDirection) {
        return;
      }

      this.sortField = nextSort.field;
      this.sortDirection = nextSort.direction;
      this.currentPage = 1;
    },
    handlePayFee(row) {
      if (this.isPayFeeSubmitting) {
        return;
      }

      this.payFeeTarget = row && typeof row === 'object' ? { ...row } : null;
      this.isPayFeeModalOpen = true;
    },
    handleEditFee(row) {
      openFeeEditModalFromRow(this, row);
    },
    handleApproveFee(row, nextApproved = null) {
      this.approveFeeTarget = row && typeof row === 'object' ? { ...row } : null;
      this.approveFeeTargetState = this.resolveApprovalTargetState(row, nextApproved);
      this.isApproveFeeModalOpen = true;
    },
    handleDeleteFee(row) {
      this.deleteFeeTarget = row && typeof row === 'object' ? { ...row } : null;
      this.isDeleteFeeModalOpen = true;
    },
    async confirmDeleteFee() {
      await confirmFeeDeleteFlow(this, {
        onSuccess: async () => {
          await this.fetchFees();
        },
      });
    },
    async confirmApproveFee() {
      await confirmFeeApproveFlow(this, {
        onSuccess: async () => {
          await this.fetchFees();
        },
      });
    },
    handleDownloadXml(row) {
      notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadXml });
    },
    handleDownloadPdf(row) {
      notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadPdf });
    },
    handleDownloadReceipt(row) {
      notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadReceipt });
    },
  },
};
</script>
